import axios from 'axios';
import { create } from 'zustand';
import { adminEshopExtractionApi } from '@api/api-config.ts';
import {
  AdminEshopExtractedProductDetailResponse,
  AdminEshopExtractedProductListResponse,
  AdminEshopExtractorResponse,
  EshopExtractedProductStatus,
  EshopProductAvailability,
} from '@api/generated/portal-be-admin';

export interface EshopExtractedProductFilters {
  extractorId?: number;
  status?: EshopExtractedProductStatus;
  availability?: EshopProductAvailability;
  /** Jen produkty, které dodavatel vyřazuje nebo doprodává. */
  endOfLife?: boolean;
  search?: string;
  page: number;
  size: number;
}

interface PaginationInfo {
  page: number;
  size: number;
  total: number;
  totalPages: number;
}

interface AdminEshopExtractedProductStoreState {
  loading: boolean;
  error: string | null;
  data: AdminEshopExtractedProductListResponse[];
  pagination: PaginationInfo | null;
  extractors: AdminEshopExtractorResponse[];
  detail: AdminEshopExtractedProductDetailResponse | null;
  detailLoading: boolean;
  loadEshopExtractedProducts: (filters: EshopExtractedProductFilters) => Promise<void>;
  loadEshopExtractedProductDetail: (id: number) => Promise<void>;
  clearDetail: () => void;
}

const describeError = (e: unknown): string =>
  axios.isAxiosError(e) ? ((e.response?.data as { message?: string })?.message ?? e.message) : String(e);

export const useAdminEshopExtractedProductStore = create<AdminEshopExtractedProductStoreState>((set, get) => ({
  loading: false,
  error: null,
  data: [],
  pagination: null,
  extractors: [],
  detail: null,
  detailLoading: false,

  loadEshopExtractedProducts: async filters => {
    set({ loading: true, error: null });
    try {
      const [{ data: page }, extractors] = await Promise.all([
        adminEshopExtractionApi.getEshopExtractedProducts({
          extractorId: filters.extractorId,
          status: filters.status,
          availability: filters.availability,
          endOfLife: filters.endOfLife || undefined,
          search: filters.search,
          page: filters.page,
          size: filters.size,
        }),
        // Vytěžovače jen kvůli filtru — stačí jednou.
        get().extractors.length > 0
          ? Promise.resolve(get().extractors)
          : adminEshopExtractionApi.getEshopExtractors().then(r => r.data),
      ]);
      set({
        loading: false,
        data: page.items ?? [],
        extractors,
        pagination: { page: page.page, size: page.size, total: page.total, totalPages: page.totalPages },
      });
    } catch (e) {
      set({ loading: false, error: describeError(e) });
    }
  },

  loadEshopExtractedProductDetail: async id => {
    set({ detailLoading: true, detail: null });
    try {
      const { data } = await adminEshopExtractionApi.getEshopExtractedProduct({ id });
      set({ detailLoading: false, detail: data });
    } catch (e) {
      set({ detailLoading: false, error: describeError(e) });
    }
  },

  clearDetail: () => set({ detail: null }),
}));
